import { useState } from 'react';
import { useAuth } from './hooks/useAuth';
import { useTasks } from './hooks/useTasks';
import { Login } from './components/Login';
import { AuthCallback } from './components/AuthCallback';
import { Matrix } from './components/Matrix';
import { ViewerLinkGenerator } from './components/ViewerLinkGenerator';
import { signOut } from './lib/auth';

function App() {
  const path = window.location.pathname;

  if (path.startsWith('/auth/callback')) {
    return <AuthCallback />;
  }

  return <MainApp />;
}

function MainApp() {
  const auth = useAuth();
  const isSignedIn = auth.role !== 'none';
  const readOnly = auth.role === 'viewer';
  const { tasks, loading, error, addTask, updateTask, deleteTask, moveTask, importTasks } = useTasks(isSignedIn);
  const [showViewerPanel, setShowViewerPanel] = useState(false);
  const [importing, setImporting] = useState(false);

  if (auth.loading) {
    return <div className="loading">Loading…</div>;
  }

  if (auth.role === 'none') {
    return <Login />;
  }

  const handleImport = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      if (!confirm('Importing replaces ALL current tasks with the file contents. Continue?')) return;
      setImporting(true);
      try {
        const text = await file.text();
        const json = JSON.parse(text);
        const rows = flattenExport(json);
        await importTasks(rows);
      } catch (err) {
        alert(err instanceof Error ? err.message : 'Import failed');
      } finally {
        setImporting(false);
      }
    };
    input.click();
  };

  const handleExport = () => {
    const grouped: Record<string, string[]> = { do: [], schedule: [], delegate: [], eliminate: [] };
    for (const t of [...tasks].sort((a, b) => a.position - b.position)) {
      grouped[t.quadrant].push(t.title);
    }
    const payload = {
      exportedAt: new Date().toISOString(),
      quadrants: {
        do:        { label: 'Urgent & Important',         tasks: grouped.do },
        schedule:  { label: 'Not Urgent & Important',     tasks: grouped.schedule },
        delegate:  { label: 'Urgent & Not Important',     tasks: grouped.delegate },
        eliminate: { label: 'Not Urgent & Not Important', tasks: grouped.eliminate },
      },
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `eisenhower-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="app">
      <header className="app-header">
        <h1>Eisenhower Matrix</h1>
        <div className="app-header-actions">
          {readOnly && <span className="badge">View only</span>}
          {!readOnly && (
            <>
              <button type="button" onClick={handleExport}>Export</button>
              <button type="button" onClick={handleImport} disabled={importing}>
                {importing ? 'Importing…' : 'Import'}
              </button>
              <button type="button" onClick={() => setShowViewerPanel((v) => !v)}>
                {showViewerPanel ? 'Close viewer links' : 'Viewer links'}
              </button>
            </>
          )}
          <button type="button" onClick={signOut}>Sign out</button>
        </div>
      </header>

      {showViewerPanel && !readOnly && <ViewerLinkGenerator />}

      {loading ? (
        <div className="loading">Loading tasks…</div>
      ) : error ? (
        <div className="err-box">Error: {error}</div>
      ) : (
        <Matrix
          tasks={tasks}
          readOnly={readOnly}
          onAdd={addTask}
          onUpdate={updateTask}
          onDelete={deleteTask}
          onMove={moveTask}
        />
      )}
    </div>
  );
}

// Accepts the legacy artifact JSON shape and flattens it for importTasks
function flattenExport(json: unknown): Array<{ quadrant: 'do' | 'schedule' | 'delegate' | 'eliminate'; title: string; position: number }> {
  if (typeof json !== 'object' || json === null) throw new Error('JSON is not an object');
  const j = json as { quadrants?: Record<string, { tasks?: string[] }> };
  if (!j.quadrants) throw new Error('Missing "quadrants" key');
  const rows: Array<{ quadrant: 'do' | 'schedule' | 'delegate' | 'eliminate'; title: string; position: number }> = [];
  for (const q of ['do', 'schedule', 'delegate', 'eliminate'] as const) {
    const list = j.quadrants[q]?.tasks ?? [];
    if (!Array.isArray(list)) throw new Error(`quadrants.${q}.tasks is not an array`);
    list.forEach((title, i) => {
      if (typeof title === 'string' && title.trim()) {
        rows.push({ quadrant: q, title: title.trim(), position: i });
      }
    });
  }
  return rows;
}

export default App;
